import { ButtonClickAnalyticsType, WebcastEntry } from "@kaltura/mediaspace-shared-types";
import styled from "@emotion/styled";
import { Typography } from "@kaltura/ds-react-components";
import { SlimEventListItem } from "@kaltura/mediaspace-shared-event-platform-event-list";
import { HtmlReactParser, translate } from "@kaltura/mediaspace-shared-utils";
import { useEffect, useState } from "react";
import { PostRegistrationStepProps } from "../PostRegistration";
import { RsvpButton, RsvpStatus } from "@kaltura/mediaspace-shared-entry";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { Truncate, TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";

export interface RsvpListProps extends PostRegistrationStepProps {
    /**
     * The header text for the list.
     */
    header?: string;
    /**
     * The subheader text for the list.
     */
    subheader?: string;
    /**
     * The list of webcast entries to display.
     */
    entries: WebcastEntry[];
    /**
     * The ks of the registered user.
     */
    ks: string;
    calendarEntriesToken?: string;
}

const ListContainer = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
}));

const StyledHeaderContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    rowGap: theme.spacing(3),
    alignItems: "center",
    textAlign: "center",
    marginBottom: theme.spacing(5),
    wordBreak: "break-word",
    [theme.breakpoints.down("md")]: {
        marginBottom: theme.spacing(3),
    },
}));

const StyledHeader = styled(Typography)(({ theme }) => ({
    margin: 0,
}));

const StyledItemsContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    rowGap: theme.spacing(2),
}));

/**
 * RsvpList component
 *
 * This component displays a list of RSVP items with a header and subheader.
 * Each item in the list includes RSVP button that allows the user to toggle his RSVP status.
 * The component also updates the step message based on the number of accepted RSVPs.
 *
 */
export const RsvpList = ({
    header = translate("Personalize your event experience"),
    subheader = translate(
        "Which sessions catch your eye?<br/>Register for the ones that you just can't miss, and we'll send calendar invites to keep you on schedule"
    ),
    entries,
    ks,
    updateStepMessage,
    calendarEntriesToken,
}: RsvpListProps) => {
    const [rsvpStatuses, setRsvpStatuses] = useState<Record<string, RsvpStatus | undefined>>({});

    // callback function to handle changes in RSVP status for each entry.
    const onItemRsvpChange = (entryId: string, rsvpStatus: RsvpStatus | undefined) => {
        setRsvpStatuses((prev) => {
            return { ...prev, [entryId]: rsvpStatus };
        });
    };

    // useEffect hook to update the step message whenever the total number of accepted RSVPs changes.
    // If there are any accepted RSVPs, it sets the step message to display the count of accepted RSVPs.
    // If there are no accepted RSVPs, it sets the step message to undefined.
    useEffect(() => {
        const acceptedCount = Object
            .values(rsvpStatuses)
            .filter((status) => status !== undefined && status !== RsvpStatus.UNCONFIRMED).length;
        updateStepMessage?.(acceptedCount > 0 ? translate("%1 RSVP accepted", [acceptedCount]) : undefined);
    }, [rsvpStatuses, updateStepMessage]);

    // useEffect hook to initialize the RSVP statuses when the component mounts.
    useEffect(() => {
        setRsvpStatuses((prev) => {
            if (Object.keys(prev).length > 0) return prev;

            const initial: Record<string, RsvpStatus> = {};
            entries.forEach((entry) => {
                initial[entry.id] = RsvpStatus.UNCONFIRMED;
            });
            return initial;
        });
    }, [entries]);

    const sendButtonAnalytics = useButtonAnalytics();

    return (
        <ListContainer>
            <StyledHeaderContainer>
                <TruncateWithTooltip text={header} lines={3}>
                    <StyledHeader variant={"h2"} className={"kms-ds-rsvp-list-header"}>
                        {header}
                    </StyledHeader>
                </TruncateWithTooltip>
                <Typography variant={"body1"} className={"kms-ds-rsvp-list-sub-header"}>
                    <Truncate lines={3}>{HtmlReactParser(subheader)}</Truncate>
                </Typography>
            </StyledHeaderContainer>
            <StyledItemsContainer>
                {entries.map((entry) => (
                    <SlimEventListItem
                        key={entry.id}
                        entry={entry}
                        onShowInfo={() =>
                            sendButtonAnalytics(
                                "Show session info",
                                ButtonClickAnalyticsType.EXPAND,
                                entry.id,
                                "RSVP",
                                "RSVP"
                            )
                        }
                        callToActionButton={
                            <RsvpButton
                                size={"large"}
                                entry={entry}
                                ks={ks}
                                initialStatus={rsvpStatuses[entry.id] ?? RsvpStatus.UNCONFIRMED}
                                isLoading={rsvpStatuses[entry.id] === undefined}
                                onChange={(status) => onItemRsvpChange(entry.id, status)}
                                onToggleClicked={() => onItemRsvpChange?.(entry.id, undefined)}
                                analyticsValue={{ value: "Post registration RSVP" }}
                                calendarEntriesToken={calendarEntriesToken}
                            />
                        }
                    />
                ))}
            </StyledItemsContainer>
        </ListContainer>
    );
};

export default RsvpList;
